import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { Upload, FileSpreadsheet, CheckCircle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import CustomButton from '../ui/CustomButton';

const ImportItemsDialog = ({ open, onClose, onImport, isDarkMode }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(fileExtension)) {
      setError('Please upload an Excel (.xlsx, .xls) or CSV (.csv) file');
      return;
    }

    setFile(selectedFile);
    setError('');
    setSuccess('');
    parseFile(selectedFile);
  };

  const parseFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setError('The file is empty or has no valid data');
          return;
        }

        // Map the data to our item structure
        const mappedItems = jsonData.map((row) => {
          return {
            Type: row['Type'] || row['Item Type'] || row['type'] || '',
            ItemDescription: row['ItemDescription'] || row['Item Description'] || row['Description'] || '',
            Quantity: String(row['Quantity'] || row['quantity'] || ''), // Ensure Quantity is always a string
            GoodsValue: parseFloat(row['GoodsValue'] || row['Goods Value'] || row['Value'] || 0),
            Currency: row['Currency'] || row['currency'] || 'USD',
            Origin: row['Origin'] || row['origin'] || '',
            StandardRefNumber: row['StandardRefNumber'] || row['Standard Reference Number'] || row['Standard Ref Number'] || '',
            Brand: row['Brand'] || row['brand'] || '',
            Packing: row['Packing'] || row['Packing Method'] || row['packing'] || '',
            BatchNumber: row['BatchNumber'] || row['Batch Number'] || row['batch'] || '',
            SlotDate: row['SlotDate'] || row['Slaughter Date'] || row['slotDate'] || '',
            ProductionDate: row['ProductionDate'] || row['Production Date'] || row['productionDate'] || '',
            ExpirationDate: row['ExpirationDate'] || row['Expiration Date'] || row['expirationDate'] || '',
          };
        });

        setPreviewData(mappedItems);
        setSuccess(`Successfully parsed ${mappedItems.length} item(s) from the file`);
      } catch (error) {
        console.error('Error parsing file:', error);
        setError(`Error parsing file: ${error.message}`);
      }
    };

    reader.onerror = () => {
      setError('Error reading file');
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImport = () => {
    if (previewData.length === 0) {
      setError('No items to import');
      return;
    }

    onImport(previewData);
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setError('');
    setSuccess('');
    onClose();
  };

  const handleDownloadTemplate = () => {
    // Create sample data with ALL available item types
    const templateData = [
      {
        'Type': 'Textile',
        'ItemDescription': '100% Cotton Fabric, White Color',
        'Quantity': '1000 KG',
        'GoodsValue': 5000,
        'Currency': 'USD',
        'Origin': 'China',
        'StandardRefNumber': 'ISO 9001',
        'Brand': 'N/A',
        'Packing': 'Cartons',
        'BatchNumber': '',
        'SlotDate': '',
        'ProductionDate': 'January 2024',
        'ExpirationDate': 'January 2026',
      },
      {
        'Type': 'Construction',
        'ItemDescription': 'Portland Cement Type II',
        'Quantity': '500 BAGS',
        'GoodsValue': 3500,
        'Currency': 'USD',
        'Origin': 'Turkey',
        'StandardRefNumber': 'ASTM C150',
        'Brand': 'Eagle',
        'Packing': 'Pallets',
        'BatchNumber': 'BATCH-2024-001',
        'SlotDate': '',
        'ProductionDate': 'February 2024',
        'ExpirationDate': '',
      },
      {
        'Type': 'Safety',
        'ItemDescription': 'Safety Helmets, Yellow',
        'Quantity': '200 UNITS',
        'GoodsValue': 1200,
        'Currency': 'EUR',
        'Origin': 'Germany',
        'StandardRefNumber': 'EN 397',
        'Brand': '3M',
        'Packing': 'Boxes',
        'BatchNumber': '',
        'SlotDate': '',
        'ProductionDate': 'March 2024',
        'ExpirationDate': '',
      },
      {
        'Type': 'Food',
        'ItemDescription': 'Organic Olive Oil, Extra Virgin',
        'Quantity': '500 LITERS',
        'GoodsValue': 2800,
        'Currency': 'USD',
        'Origin': 'Italy',
        'StandardRefNumber': 'ISO 22000',
        'Brand': 'Mediterranean',
        'Packing': 'Bottles',
        'BatchNumber': 'FOOD-2024-003',
        'SlotDate': '',
        'ProductionDate': 'March 2024',
        'ExpirationDate': 'March 2026',
      },
      {
        'Type': 'Chemical',
        'ItemDescription': 'Industrial Cleaning Solution',
        'Quantity': '200 LITERS',
        'GoodsValue': 1800,
        'Currency': 'USD',
        'Origin': 'USA',
        'StandardRefNumber': 'ISO 14001',
        'Brand': 'ChemClean',
        'Packing': 'Drums',
        'BatchNumber': 'CHEM-2024-002',
        'SlotDate': '',
        'ProductionDate': 'February 2024',
        'ExpirationDate': 'February 2027',
      },
      {
        'Type': 'Engineering',
        'ItemDescription': 'Hydraulic Pump, High Pressure',
        'Quantity': '50 UNITS',
        'GoodsValue': 8500,
        'Currency': 'USD',
        'Origin': 'Japan',
        'StandardRefNumber': 'ISO 9001',
        'Brand': 'TechFlow',
        'Packing': 'Crates',
        'BatchNumber': 'ENG-2024-001',
        'SlotDate': '',
        'ProductionDate': 'January 2024',
        'ExpirationDate': '',
      },
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Type
      { wch: 35 }, // ItemDescription
      { wch: 12 }, // Quantity
      { wch: 12 }, // GoodsValue
      { wch: 10 }, // Currency
      { wch: 15 }, // Origin
      { wch: 20 }, // StandardRefNumber
      { wch: 15 }, // Brand
      { wch: 15 }, // Packing
      { wch: 15 }, // BatchNumber
      { wch: 12 }, // SlotDate
      { wch: 15 }, // ProductionDate
      { wch: 15 }, // ExpirationDate
    ];

    // Add data validation for Type column (dropdown)
    const typeOptions = ['Textile', 'Construction', 'Safety', 'Food', 'Chemical', 'Engineering'];
    
    // Create a range for the Type column (excluding header)
    const typeColumnRange = {
      s: { c: 0, r: 1 }, // Start at row 2 (after header)
      e: { c: 0, r: templateData.length + 10 } // End at row 20 (allowing for more entries)
    };

    // Add data validation
    if (!ws['!dataValidation']) {
      ws['!dataValidation'] = [];
    }
    
    ws['!dataValidation'].push({
      type: 'list',
      allowBlank: false,
      showInputMessage: true,
      promptTitle: 'Item Type Selection',
      prompt: 'Please select an item type from the dropdown list',
      showErrorMessage: true,
      errorTitle: 'Invalid Selection',
      error: 'Please select a valid item type from the dropdown',
      formula1: `"${typeOptions.join(',')}"`,
      sqref: XLSX.utils.encode_range(typeColumnRange)
    });

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Items');

    // Generate file and trigger download
    XLSX.writeFile(wb, 'Certificate_Items_Template.xlsx');
  };

  const getCompletionStatus = (item) => {
    const requiredFields = ['Type', 'ItemDescription', 'Quantity', 'GoodsValue', 'Origin', 'StandardRefNumber', 'Packing'];
    const filledFields = requiredFields.filter(field => {
      const value = item[field];
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'number') return value > 0;
      return !!value;
    });
    return {
      percentage: Math.round((filledFields.length / requiredFields.length) * 100),
      filled: filledFields.length,
      total: requiredFields.length,
    };
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FileSpreadsheet size={24} color="#10b981" />
            <Typography
              variant="h6"
              sx={{
                ml: 1,
                fontWeight: 600,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              Import Certificate Items
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download size={16} />}
            onClick={handleDownloadTemplate}
            sx={{
              borderColor: '#3b82f6',
              color: '#3b82f6',
              '&:hover': {
                borderColor: '#2563eb',
                background: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            Download Template
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ p: 2 }}>
          {/* Instructions */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>File Format Requirements:</strong>
            </Typography>
            <Typography variant="caption" component="div">
              • Excel (.xlsx, .xls) or CSV (.csv) file<br />
              • Required columns: Type, ItemDescription, Quantity, GoodsValue, Origin, StandardRefNumber, Packing<br />
              • Optional columns: Currency, Brand, BatchNumber, SlotDate, ProductionDate, ExpirationDate<br />
              • Available Item Types: Textile, Construction, Safety, Food, Chemical, Engineering<br />
              • Quantity can be numbers (1000) or text with units (1000 KG, 500 BAGS)<br />
              • Column names are case-insensitive<br />
              • <strong>Tip:</strong> Click "Download Template" above to get a sample file with dropdown menus for Type selection
            </Typography>
          </Alert>

          {/* File Upload Section */}
          <Box
            sx={{
              mb: 3,
              p: 3,
              border: '2px dashed',
              borderColor: isDarkMode ? '#475569' : '#d1d5db',
              borderRadius: 2,
              textAlign: 'center',
              backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.3)' : 'rgba(249, 250, 251, 0.5)',
            }}
          >
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="items-file-upload"
            />
            <label htmlFor="items-file-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<Upload size={18} />}
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  },
                }}
              >
                Choose File
              </Button>
            </label>
            {file && (
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  color: isDarkMode ? '#94a3b8' : '#6b7280',
                }}
              >
                Selected: <strong>{file.name}</strong>
              </Typography>
            )}
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Preview Data Table */}
          {previewData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CheckCircle size={20} style={{ marginRight: 8, color: '#10b981' }} />
                Preview ({previewData.length} items)
              </Typography>

              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 400,
                  background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.8)',
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Origin</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((item, index) => {
                      const status = getCompletionStatus(item);
                      return (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {item.Type ? (
                              <Chip label={item.Type} size="small" color="primary" />
                            ) : (
                              <Chip label="Missing" size="small" color="error" />
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.ItemDescription || '-'}
                          </TableCell>
                          <TableCell>{item.Quantity || '-'}</TableCell>
                          <TableCell>
                            {item.GoodsValue > 0 ? `${item.GoodsValue} ${item.Currency}` : '-'}
                          </TableCell>
                          <TableCell>{item.Origin || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={`${status.percentage}%`}
                              size="small"
                              sx={{
                                background:
                                  status.percentage === 100
                                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                    : status.percentage >= 70
                                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                color: 'white',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Note:</strong> Items with missing required fields can still be imported, but you'll need to complete them manually before saving the certificate.
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <CustomButton variant="outlined" onClick={handleClose}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="gradient"
          onClick={handleImport}
          disabled={previewData.length === 0}
          startIcon={<Upload size={16} />}
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            },
          }}
        >
          Import {previewData.length} Item(s)
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ImportItemsDialog;
